import { useState, useCallback, useDeferredValue, useEffect, useRef } from "react";
import {
  convertToExcalidrawElements,
} from "@excalidraw/excalidraw";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import CustomTest from "./CustomTest.tsx";
import ExcalidrawWrapper from "./ExcalidrawWrapper.tsx";
import Testcases from "./Testcases.tsx";
import { parseMermaid } from "../src/parseMermaid.ts";
import { parseMarkdownToExcalidraw } from "../src/markdownToExcalidraw.ts";

import "@excalidraw/excalidraw/index.css";

export interface MermaidData {
  definition: string;
  output: any | null;
  error: string | null;
}

export type ActiveTestCaseIndex = number | "custom" | null;

const THEME_STORAGE_KEY = "mermaid-to-excalidraw-theme";

type ThemeMode = "light" | "dark";

interface ThemeState {
  mode: ThemeMode;
  isUserPreference: boolean;
}

const getSystemThemeMode = (): ThemeMode => {
  if (typeof window === "undefined") {
    return "light";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const readStoredThemeMode = (): ThemeMode | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedThemeMode = window.sessionStorage.getItem(THEME_STORAGE_KEY);
    return storedThemeMode === "dark" || storedThemeMode === "light"
      ? storedThemeMode
      : null;
  } catch (error) {
    console.error("Failed to read theme from sessionStorage:", error);
    return null;
  }
};

const writeStoredThemeMode = (mode: ThemeMode) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (error) {
    console.error("Failed to save theme to sessionStorage:", error);
  }
};

export type Page = {
  id: string;
  name: string;
  definition: string;
  elements?: any[];
  appState?: any;
  lastParsedDefinition?: string;
};

const App = () => {
  const excalidrawAPIRef = useRef<ExcalidrawImperativeAPI | null>(null);

  const [pages, setPages] = useState<Page[]>(() => {
    const stored = localStorage.getItem("mermaid-to-excalidraw-pages");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error("Failed to parse stored pages", e);
      }
    }
    return [
      {
        id: "default",
        name: "Main Roadmap",
        definition: `mindmap
  root((Sagamuda App))
    Public
    Auth
    Member
    Admin
    Services`,
      },
    ];
  });
  const [activePageId, setActivePageId] = useState<string>(() => {
    const stored = localStorage.getItem("mermaid-to-excalidraw-active-page");
    return stored || "default";
  });
  const currentRequestRef = useRef(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    localStorage.setItem("mermaid-to-excalidraw-active-page", activePageId);
  }, [activePageId]);

  const activePage = pages.find((p) => p.id === activePageId) || pages[0];

  const [mermaidData, setMermaidData] = useState<MermaidData>({
    definition: activePage.definition,
    error: null,
    output: null,
  });

  const [activeTestCaseIndex, setActiveTestCaseIndex] =
    useState<ActiveTestCaseIndex>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [themeState, setThemeState] = useState<ThemeState>(() => {
    const storedThemeMode = readStoredThemeMode();
    if (storedThemeMode) {
      return {
        mode: storedThemeMode,
        isUserPreference: true,
      };
    }

    return {
      mode: getSystemThemeMode(),
      isUserPreference: false,
    };
  });

  const isDarkMode = themeState.mode === "dark";
  const deferredMermaidData = useDeferredValue(mermaidData);

  useEffect(() => {
    try {
      localStorage.setItem("mermaid-to-excalidraw-pages", JSON.stringify(pages));
    } catch (e) {
      console.warn("LocalStorage full, cleaning up old session data...");
      // Optional: implement cleanup logic here if needed
    }
  }, [pages]);

  const addPage = useCallback((name?: string, definition?: string) => {
    const newId = `page-${Date.now()}`;
    const newPage = {
      id: newId,
      name: name || `Untitled ${pages.length + 1}`,
      definition: definition || "mindmap\n  root((New Topic))",
    };
    setPages((prev) => [...prev, newPage]);
    setActivePageId(newId);
  }, [pages.length]);

  const deletePage = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (pages.length <= 1) {
       alert("You must have at least one page.");
       return;
    }
    if (confirm("Are you sure you want to delete this page?")) {
      setPages((prev) => {
        const filtered = prev.filter((p) => p.id !== id);
        if (activePageId === id) {
          setActivePageId(filtered[0].id);
        }
        return filtered;
      });
    }
  };

  const updateActivePageDefinition = useCallback((definition: string) => {
    setPages((prev) =>
      prev.map((p) => (p.id === activePageId ? { ...p, definition } : p))
    );
  }, [activePageId]);

  const updateActivePageName = (name: string) => {
    setPages((prev) =>
      prev.map((p) => (p.id === activePageId ? { ...p, name } : p))
    );
  };

  const handleCanvasChange = useCallback((elements: any[], appState: any) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    
    saveTimeoutRef.current = setTimeout(() => {
      setPages((prev) => 
        prev.map((p) => (p.id === activePageId ? { 
          ...p, 
          elements,
          appState: {
            zoom: appState.zoom,
            scrollX: appState.scrollX,
            scrollY: appState.scrollY,
          }
        } : p))
      );
    }, 300); // 300ms debounce for performance
  }, [activePageId]);

  const toggleTheme = useCallback(() => {
    setThemeState((currentThemeState: ThemeState) => {
      const nextThemeMode =
        currentThemeState.mode === "dark" ? "light" : "dark";
      writeStoredThemeMode(nextThemeMode);

      return {
        mode: nextThemeMode,
        isUserPreference: true,
      };
    });
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (isSidebarCollapsed) {
      document.body.classList.add("sidebar-collapsed");
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
  }, [isSidebarCollapsed]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setThemeState((currentThemeState: ThemeState) => {
        if (currentThemeState.isUserPreference) {
          return currentThemeState;
        }

        return {
          mode: e.matches ? "dark" : "light",
          isUserPreference: false,
        };
      });
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.shiftKey && event.key === "D") {
        event.preventDefault();
        toggleTheme();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleTheme]);

  const handleInsertMermaidSvg = useCallback(
    (svgHtml: string, width: number, height: number) => {
      const api = excalidrawAPIRef.current;
      if (!api || api.isDestroyed) {
        return;
      }

      const dataURL = `data:image/svg+xml;base64,${btoa(
        unescape(encodeURIComponent(svgHtml))
      )}`;
      const fileId = `mermaid-svg-${Date.now()}`;

      api.addFiles([
        {
          id: fileId as any,
          dataURL: dataURL as any,
          mimeType: "image/svg+xml" as any,
          created: Date.now(),
          lastRetrieved: Date.now(),
        },
      ]);

      api.updateScene({
        elements: [
          ...api.getSceneElements(),
          ...convertToExcalidrawElements([
            {
              type: "image",
              fileId: fileId as any,
              width,
              height,
              x: 0,
              y: 0,
            },
          ]),
        ],
      });
      api.scrollToContent(api.getSceneElements(), { fitToContent: true });
    },
    []
  );

  const handleFocusContent = useCallback(() => {
    const api = excalidrawAPIRef.current;
    if (api && !api.isDestroyed) {
      api.scrollToContent(api.getSceneElements(), { fitToContent: true });
    }
  }, []);

  const handleOnChange = useCallback(
    async (
      definition: MermaidData["definition"],
      activeTestCaseIndex: ActiveTestCaseIndex
    ) => {
      const requestId = ++currentRequestRef.current;
      try {
        setActiveTestCaseIndex(activeTestCaseIndex);
        updateActivePageDefinition(definition);

        const healedDefinition = fixMojibake(definition);
        
        if (activePage.elements && activePage.lastParsedDefinition === healedDefinition) {
          // Temporarily removed short circuit to ensure "Render to Canvas" always re-renders
          // even if the text hasn't changed.
        }

        const isMarkdown = 
          healedDefinition.includes("# ") || 
          healedDefinition.includes("```") || 
          healedDefinition.includes("> ") || 
          healedDefinition.includes("|") || 
          /^([-*]|\d+\.)\s/m.test(healedDefinition);

        let output: any;
        if (isMarkdown) {
          const elements = await parseMarkdownToExcalidraw(healedDefinition);
          output = { type: "markdown" as const, elements };
        } else {
          output = await parseMermaid(healedDefinition);
        }

        if (requestId === currentRequestRef.current) {
          setMermaidData({
            definition,
            output,
            error: null,
          });

          setPages(prev => prev.map(p => p.id === activePageId ? { 
            ...p, 
            elements: (output as any).elements, 
            lastParsedDefinition: healedDefinition 
          } : p));
        }

      } catch (error) {
        if (requestId === currentRequestRef.current) {
          console.error("Parse Error:", error);
          setMermaidData((prev) => ({
            ...prev,
            error: error instanceof Error ? error.message : String(error),
            // Keep previous output to avoid flicker, or set to null if preferred
          }));
        }
      }
    },
    [updateActivePageDefinition, activePageId]
  );

  const handleImportMarkdown = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        addPage(file.name.replace(/\.md$/, ""), content);
      };
      reader.readAsText(file, "UTF-8");
    }
  };

  useEffect(() => {
    handleOnChange(activePage.definition, "custom");
  }, [activePageId, handleOnChange]);

  return (
    <>
      <div className="left-side">
        <header className="playground-hero">
          <div className="playground-hero-copy">
            <p className="playground-kicker">{"Mermaid to Excalidraw"}</p>
          </div>
        </header>

        <section className="pages-section">
           <div className="panel-header">
             <p className="panel-kicker">{"Files / Pages"}</p>
             <div style={{ display: "flex", gap: "8px" }}>
               <label className="add-page-btn" title="Import Markdown">
                 📥
                 <input type="file" accept=".md" onChange={handleImportMarkdown} style={{ display: "none" }} />
               </label>
               <button className="add-page-btn" onClick={() => addPage()} title="New Page">+</button>
             </div>
           </div>
           <div className="pages-list">
             {pages.map((page) => (
               <div 
                 key={page.id} 
                 className={`page-item ${activePageId === page.id ? "active" : ""}`}
                 onClick={() => setActivePageId(page.id)}
               >
                 <span className="page-icon">📄</span>
                 <input 
                   className="page-name-input"
                   value={page.name}
                   onChange={(e) => updateActivePageName(e.target.value)}
                   onClick={(e) => e.stopPropagation()}
                 />
                 <button 
                   className="delete-page-btn" 
                   onClick={(e) => deletePage(page.id, e)}
                   title="Delete Page"
                 >
                   🗑️
                 </button>
               </div>
             ))}
           </div>
        </section>

        <section id="custom-test" className="">
          <CustomTest
            activeTestCaseIndex={activeTestCaseIndex}
            mermaidData={deferredMermaidData}
            onChange={handleOnChange}
          />
        </section>

        <section className="playground-panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">{"Examples"}</p>
            </div>
          </div>
          <Testcases
            activeTestCaseIndex={activeTestCaseIndex}
            onChange={handleOnChange}
            onInsertMermaidSvg={handleInsertMermaidSvg}
          />
        </section>
      </div>
      <div id="excalidraw">
        <div className="canvas-watermark">draw.sagamuda.id</div>
        
        <div className="canvas-controls">
          <button
            className="playground-button-icon"
            id="focus-content-btn"
            onClick={handleFocusContent}
            title="Focus to Content (Center View)"
          >
            🎯
          </button>

          <button
            className="playground-button-icon"
            id="dark-mode-toggle"
            onClick={toggleTheme}
            title={
              isDarkMode
                 ? "Switch to light mode (Alt+Shift+D)"
                 : "Switch to dark mode (Alt+Shift+D)"
            }
          >
            {isDarkMode ? "☀️" : "🌙"}
          </button>

          <button
            className="playground-button-icon"
            id="sidebar-toggle"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title={isSidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}
          >
            {isSidebarCollapsed ? "→" : "←"}
          </button>
        </div>

        <ExcalidrawWrapper
          mermaidDefinition={deferredMermaidData.definition}
          mermaidOutput={deferredMermaidData.output}
          theme={isDarkMode ? "dark" : "light"}
          apiRef={excalidrawAPIRef}
          onChange={handleCanvasChange}
          initialAppState={activePage.appState}
        />
      </div>
    </>
  );
};

function fixMojibake(text: string): string {
  if (!text) return "";
  
  // Mapping of common Windows-1252 / CP1252 characters to their original byte values
  // This is crucial because chars like 'œ' (U+0153) are actually byte 0x9C in CP1252.
  const cp1252Map: Record<string, number> = {
    '\u20AC': 0x80, '\u201A': 0x82, '\u0192': 0x83, '\u201E': 0x84, '\u2026': 0x85, '\u2020': 0x86, '\u2021': 0x87,
    '\u02C6': 0x88, '\u2030': 0x89, '\u0160': 0x8A, '\u2039': 0x8B, '\u0152': 0x8C, '\u017D': 0x8E,
    '\u2018': 0x91, '\u2019': 0x92, '\u201C': 0x93, '\u201D': 0x94, '\u2022': 0x95, '\u2013': 0x96, '\u2014': 0x97,
    '\u02DC': 0x98, '\u2122': 0x99, '\u0161': 0x9A, '\u203A': 0x9B, '\u0153': 0x9C, '\u017E': 0x9E, '\u0178': 0x9F
  };

  // Match any sequence of non-ASCII characters
  const nonAsciiPattern = /[^\x00-\x7F]+/g;
  
  try {
    return text.replace(nonAsciiPattern, (match) => {
      try {
        const bytes = new Uint8Array(match.length);
        for (let i = 0; i < match.length; i++) {
          const char = match[i];
          // Use mapping for CP1252 specifics, or just take the low byte for Latin-1
          bytes[i] = cp1252Map[char] !== undefined ? cp1252Map[char] : (char.charCodeAt(0) & 0xFF);
        }
        // Attempt to decode the byte sequence as UTF-8
        return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
      } catch {
        // If it's not a valid UTF-8 sequence, keep the original characters
        return match;
      }
    });
  } catch {
    return text;
  }
}

export default App;
